/* eslint-disable import/no-cycle */
import logo from '@assets/images/logo.png';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import './styles.css';

import { Button, ComboBox } from '@components';
import { mdiMagnify } from '@mdi/js';
import {
  Autocomplete,
  Avatar,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled } from '@mui/material/styles';
import { searchValues, getNotificationTime } from '@utils';
import { useDispatch, useSelector } from 'react-redux';

import { alteraPlano, getMensageria } from '../../../store/user/thunks';
import { getPerfilInvestimento } from '../../../store/plan/thunks';
import {
  useUserData,
  useDadosPessoais,
  useIsLogged,
} from '../../../store/hooks';

import { useAuth } from '../../../pages/Shell';
import { HOME } from '../../utils/paths';
import { baseWsMensageria } from '../../services/env-variables';
import Icons from '../Icon';
import { Relogio } from '../Relogio';

const NOTIFICATION_TABS = ['Todas'];

const CustomizedAutocomplete = styled((props) => (
  <Autocomplete {...props} inputMode="search" />
))`
  .MuiOutlinedInput-notchedOutline {
    border-color: var(--primary-color);
    border-radius: 25px;
  }
`;

export const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const auth = useAuth();
  const userData = useUserData();
  const dadosPessoais = useDadosPessoais();
  const isLogged = useIsLogged();
  const custom = useSelector((state) => state.configs.tema);
  const telasDisponiveis = useSelector(
    (state) => state.configs.telasDisponiveis.data
  );
  const perfilInvestimento = useSelector(
    (state) => state.plan.perfilInvestimento
  );
  const mensageria = useSelector((state) => state.user.mensageria);
  const mensagensPendentes = useSelector(
    (state) => state.user.mensagensPendentes
  );

  const wsRef = useRef(null);

  const homologacaoHostnames = ['localhost', 'brasil', '10.2.103.139'];
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isHomologacao = homologacaoHostnames.some((host) =>
    hostname.includes(host)
  );

  const [modoSimulacao, setModoSimulacao] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('0');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('Todas');
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const msgIcon = mensagensPendentes ? 'notifications_unread' : 'notifications';

  const filteredOptions = useMemo(
    () =>
      searchValues.filter((option) => {
        const alwaysShow = option.id.length === 0;
        const screenEnabled = option.id.some((id) => telasDisponiveis?.[id]);
        return alwaysShow || screenEnabled;
      }),
    [telasDisponiveis]
  );

  const alteraValoresStore = useCallback(
    (plan) => {
      dispatch(alteraPlano(plan));
    },
    [dispatch]
  );

  const logout = () => {
    auth.signout();
  };

  const handleOpenNotifications = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const planos = dadosPessoais.planoPrevidencia || [];

    if (!planos.length) {
      return;
    }

    const planoAtualId =
      userData.plano || planos[0]?.id?.toString() || planos[0]?.id;

    const currentPlan = planos.find(
      (plan) => plan.id.toString() === planoAtualId.toString()
    );

    if (currentPlan) {
      setSelectedPlan(currentPlan.id.toString());
    }
  }, [userData.plano, dadosPessoais.planoPrevidencia]);

  useEffect(() => {
    const idLocalStorage = localStorage.getItem('Id');

    if (isLogged && idLocalStorage && userData.matricula) {
      setModoSimulacao(Number(idLocalStorage) !== Number(userData.matricula));
      return;
    }

    setModoSimulacao(false);
  }, [isLogged, userData.matricula]);

  useEffect(() => {
    if (
      isLogged &&
      userData.matricula &&
      userData.plano &&
      !perfilInvestimento.loading &&
      !perfilInvestimento.data?.Label
    ) {
      dispatch(getPerfilInvestimento());
    }
  }, [
    dispatch,
    isLogged,
    userData.matricula,
    userData.plano,
    perfilInvestimento.loading,
    perfilInvestimento.data,
  ]);

  useEffect(() => {
    if (!isLogged || !userData.matricula || !userData.token) {
      return undefined;
    }

    dispatch(getMensageria());

    try {
      const ws = new WebSocket(baseWsMensageria);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            matricula: userData.matricula,
            token: userData.token,
            aplicacao: 'Web',
          })
        );
      };
      ws.onmessage = () => {
        dispatch(getMensageria());
      };
      ws.onclose = () => {
        wsRef.current = null;
      };
    } catch (err) {
      // websocket pode falhar em ambientes sem suporte; ignora silenciosamente
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [dispatch, isLogged, userData.matricula, userData.token]);

  const renderNotificationsPopover = () => (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={handleCloseNotifications}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          maxWidth: '350px',
          maxHeight: 520,
          borderRadius: '12px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          mt: 1,
          overflow: 'hidden',
        },
      }}
    >
      <div className="notification-popover-header">
        <h3 className="notification-popover-title">Notificações</h3>
        <IconButton
          size="small"
          onClick={handleCloseNotifications}
          sx={{
            border: '1px solid #ddd',
            width: 28,
            height: 28,
          }}
        >
          <span style={{ fontSize: '14px', color: '#666' }}>✕</span>
        </IconButton>
      </div>

      <div className="notification-tabs">
        {NOTIFICATION_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`notification-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="notification-popover-list">
        {mensageria?.length === 0 ? (
          <div className="notification-empty">
            <p>Sem notificações no momento.</p>
          </div>
        ) : (
          mensageria?.map((notification) => (
            <NavLink
              key={notification.idMsg}
              to="/mensageria"
              state={{ params: { id: notification.idMsg } }}
              className="notification-item-link"
              onClick={handleCloseNotifications}
            >
              <div className="notification-item">
                <span
                  className="notification-dot"
                  style={{
                    visibility:
                      notification.Lida === null ? 'visible' : 'hidden',
                  }}
                />
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#E77708',
                    fontSize: '0.9rem',
                  }}
                />
                <div className="notification-content">
                  <p className="notification-item-title">
                    {notification.Titulo}
                  </p>
                  <span className="notification-time">
                    {getNotificationTime(notification)}
                  </span>
                </div>
              </div>
            </NavLink>
          ))
        )}
      </div>

      <div className="notification-footer">
        <NavLink
          to="/mensageria"
          onClick={handleCloseNotifications}
          className="notification-footer-link"
        >
          Acesse todas as notificações
          <ArrowForwardIcon sx={{ fontSize: '1rem', ml: 0.5 }} />
        </NavLink>
      </div>
    </Popover>
  );

  return (
    <>
      {modoSimulacao && (
        <div className="topHeader">
          <h4>FORLUZ : FORLUZ : FORLUZ : FORLUZ : FORLUZ : FORLUZ</h4>
        </div>
      )}
      <div className="header mb-32">
        <div data-tour="step-0">
          <Link to={HOME}>
            <img className="logo" src={logo} alt="Forluz logo" />
          </Link>
          <div className="relogio">
            <div className="d-none d-lg-flex align-items-center">
              <Relogio />
            </div>
          </div>
        </div>

        <div
          className="d-none d-lg-flex align-items-end gap-3"
          data-tour="step-1"
        >
          {isHomologacao && (
            <div className="d-none d-lg-flex align-items-center">
              <Button
                className="force-orange-color"
                onClick={() => {
                  navigate(HOME);
                }}
              >
                {userData.matricula}
              </Button>
            </div>
          )}
          {Array.isArray(dadosPessoais.planoPrevidencia) &&
            dadosPessoais.planoPrevidencia.length > 0 && (
              <ComboBox
                color={custom.nomeCorPrincipal}
                options={dadosPessoais.planoPrevidencia.map((x) => ({
                  value: x.id.toString(),
                  label: x.name,
                }))}
                value={selectedPlan}
                onChange={(value) => {
                  alteraValoresStore(value);
                  setSelectedPlan(value);
                }}
              />
            )}
          <CustomizedAutocomplete
            size="small"
            freeSolo
            options={filteredOptions.map((option) => option.title)}
            onChange={(e, value) => {
              const route = filteredOptions.find(
                (op) => op.title === value
              )?.path;

              if (route) {
                navigate(route);
              }
            }}
            onInputChange={(e, value) => setSearch(value)}
            className="w-250"
            renderInput={(params) => (
              <TextField
                {...params}
                label="O que você procura?"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons path={mdiMagnify} color="#727272" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            value={search}
          />

          <Button
            className="only-icon-xl"
            noBorder
            color="orange"
            icon={msgIcon}
            onClick={handleOpenNotifications}
            aria-label="Notificações"
          />

          <Button
            className="only-icon me-3 mb-1"
            icon="clear"
            onClick={() => logout()}
            aria-label="Sair do sistema"
          />
        </div>

        <div className="d-flex d-lg-none align-items-center">
          {Array.isArray(dadosPessoais.planoPrevidencia) &&
            dadosPessoais.planoPrevidencia.length > 0 && (
              <ComboBox
                color={custom.nomeCorPrincipal}
                options={dadosPessoais.planoPrevidencia.map((x) => ({
                  value: x.id.toString(),
                  label: x.name,
                }))}
                value={selectedPlan}
                onChange={(value) => {
                  alteraValoresStore(value);
                  setSelectedPlan(value);
                }}
              />
            )}
          <Button
            className="only-icon-xl ms-2"
            noBorder
            color="orange"
            icon={msgIcon}
            onClick={handleOpenNotifications}
            aria-label="Notificações"
          />
          <Button
            className="only-icon ms-2"
            transparent
            color="orange"
            icon="search"
          />
          <Button
            className="only-icon ms-2 me-3"
            icon="clear"
            onClick={() => logout()}
            aria-label="Sair do sistema"
          />
        </div>

        {renderNotificationsPopover()}
      </div>
    </>
  );
};
